import React, { useState, useEffect } from "react";
import {
  ListItem,
  Grid,
  IconButton,
  Badge,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Operators } from "../../../metadata";
import FilterChildItem from "./FilterChildItem";
import { simplifyHeader } from "../../../methods";

const FilterListItem = ({
  row,
  filterKey,
  handleOpenChildFilters,
  handleDeleteFilter,
  setFilters,
  parentArray,
  parentIndex,
  filters,
  filter,
  category,
  metadata
}) => {
  const [operatorValue, setOperatorValue] = useState(row?.operator || "=");

  // Sync local operator state when row.operator changes
  useEffect(() => {
    if (row?.operator && row.operator !== operatorValue) {
      setOperatorValue(row.operator);
    }
  }, [row?.operator]);

  const handleOperatorChange = (value, rowId) => {
    setOperatorValue(value);

    setFilters((prevState) => {
      return prevState.map((filter) => {
        // Check if the filter contains the row to be updated
        if (filter.rows.some(childFilter => childFilter.id === rowId)) {
          // Map through rows and update the operator of the matching row
          const updatedRows = filter.rows.map(childFilter => {
            if (childFilter.id === rowId) {
              return { ...childFilter, operator: value };
            }
            return childFilter;
          });
          return { ...filter, rows: updatedRows };
        }
        return filter;
      });
    });
  };

  const handleCopyBelow = () => {
    // Creating a deep copy of filters
    let filtersCopy = structuredClone(filters);

    // Iterate over each category in the filtersCopy
    filtersCopy = filtersCopy.map(category => {
      if (category.key === filterKey) {
        // Create a new array for 'category.rows' with modified items
        const newRows = category.rows.map(filterItem => {
          // Check if 'filterItem' doesn't have a 'rows' property
          if (!('rows' in filterItem)) {
            // Return a new object with the 'rows' property added or modified
            return { ...filterItem, rows: structuredClone(row.rows) };
          }
          // Return the item as is if it already has 'rows'
          return filterItem;
        });

        // Return a new category object with the updated rows
        return { ...category, rows: newRows };
      }
      // Return the category as is if it doesn't match the filterKey
      return category;
    });

    // Update the state with the modified filters array
    setFilters(filtersCopy);
  };

  const handleShowCopyBelow = () => {
    // Destructuring for cleaner code
    const nextRow = parentArray?.[parentIndex + 1];

    // Using optional chaining for safer property access
    const isRowsPresent = row?.rows;
    const isNextRowWithoutRows = nextRow && !nextRow.rows;

    // If current row has 'rows' and the next row exists but doesn't have 'rows', show the icon
    if (isRowsPresent && isNextRowWithoutRows) {
      return (
        <IconButton
          edge="end"
          aria-label="copy below"
          onClick={() => {
            handleCopyBelow();
          }}
        >
          <ContentCopyIcon />
        </IconButton>
      );
    }

    // Return null when the icon should not be displayed
    return null;
  };

  const SelectOperators = () => {
    if (row?.dataType === "NUMBER") {
      return (
        <Select
          id="field-select"
          label="Field"
          onChange={(e) => handleOperatorChange(e.target.value, row.id)}
          variant="standard"
          size="small"
          value={operatorValue}
          MenuProps={{
            PaperProps: {
              sx: {
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                '& .MuiList-root': {
                  padding: 0,
                },
              },
            },
          }}
        >
          {Operators.number.map(({ name, label }) => {
            return (
              <MenuItem value={name} key={name}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      );
    } else {
      return (
        <Select
          id="field-select"
          label="Field"
          onChange={(e) => handleOperatorChange(e.target.value, row.id)}
          variant="standard"
          size="small"
          value={operatorValue}
          MenuProps={{
            PaperProps: {
              sx: {
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                '& .MuiList-root': {
                  padding: 0,
                },
              },
            },
          }}
        >
          {Operators.text.map(({ name, label }) => {
            return (
              <MenuItem value={name} key={name}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      );
    }
  };

  const maskRowValue = (value) => {
    if (value === "*") return "All";
    return value;
  }

  // Get display value based on displayKey/displayValue if available, otherwise fallback to standard value
  const getDisplayValue = () => {
    if (row.displayValue) {
      return maskRowValue(row.displayValue);
    } else if (row.displayKey && row[row.displayKey]) {
      return maskRowValue(row[row.displayKey]);
    } else if (row[filterKey]) {
      return maskRowValue(row[filterKey]);
    } else {
      return maskRowValue(row.value);
    }
  };

  // Get the raw value without masking for API calls
  const getRawValue = () => {
    if (row.displayValue) {
      return row.displayValue;
    } else if (row.displayKey && row[row.displayKey]) {
      return row[row.displayKey];
    } else if (row[filterKey]) {
      return row[filterKey];
    } else {
      return row.value;
    }
  };

  // Get the friendly name to display from the best available source
  const getDisplayName = () => {
    return row.displayFriendlyName || row.friendlyName || simplifyHeader(row.key);
  };

  // Check if the category has child filters available
  const hasChildFilters = () => {
    if (!metadata?.queryItems) return false;

    const foundCategory = metadata.queryItems.find((item) => item.category === category);
    return foundCategory?.childFilters && foundCategory.childFilters.length > 0;
  };

  return (
    <ListItem
      sx={{
        flexDirection: "row",
        flexWrap: "wrap",
        padding: "0px 6px 0px 20px",
      }}
    >
      <Grid size={10} padding={1}>
        {getDisplayName()}{" "}
        &nbsp; {SelectOperators()} &nbsp; {getDisplayValue()}
      </Grid>
      <Grid size={2}
        padding={1}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {hasChildFilters() && (
          <Tooltip title="Sub filter">
            <IconButton
              edge="end"
              color="#04aeea"
              aria-label="filter"
              onClick={() => {
                // Pass the raw value instead of the display value to prevent "All" from being sent to API
                handleOpenChildFilters(row, getRawValue(), filter, category);
              }}
            >
              <Badge badgeContent={row?.rows?.length} color="primary">
                <FilterAltIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete Filter">
          <IconButton
            edge="end"
            color="error"
            aria-label="filter"
            onClick={(e) => {
              handleDeleteFilter(row)
            }}
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid size={12} >
        <ul
          style={{
            borderLeft: "1px solid rgb(24, 144, 255)",
          }}
        >
          {row?.rows?.map((childFilter) => {
            for (var prop in childFilter) {
              return childFilter[prop].map((f, index, array) => {
                let value =
                  typeof f.value === "boolean" ? f.value.toString() : f.value;
                return (
                  <FilterChildItem
                    filter={f}
                    index={index}
                    array={array}
                    value={value}
                    key={index.toString()}
                    setFilters={setFilters}
                    currentRow={row}
                    parentArray={parentArray}
                    parentIndex={parentIndex}
                  />
                );
              });
            }
          })}
        </ul>
      </Grid>
    </ListItem>
  );
};

export default FilterListItem;